import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';

import {
  type AlertReset,
  AlertResetSchema,
} from '@/alerts/entities/alert-reset';
import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import type { AlertsFilters } from '@/types/alerts-filters';

export namespace ListAlertResets {
  export const key = (input: Input) => [
    'alerts',
    'list-alert-resets',
    input.filters,
  ];

  export type Params = { refetchInterval?: number };
  export type Input = { filters: AlertsFilters };
  export type Output = AlertReset[];

  function outputMapper(
    alertsResetsData: FeApi.ListAlertResetsViewOutput['alerts_resets'],
  ): AlertReset[] {
    if (alertsResetsData.length === 0) {
      return [];
    }

    const apiResets = alertsResetsData.map<AlertReset>((alertResets) => ({
      fullFrontendId: alertResets.full_frontend_id,
      systemFrontendId: alertResets.system_frontend_id,
      accureId: alertResets.accure_id,
      resetAtTime: new Date(alertResets.reset_at_time),
    }));

    const resets = AlertResetSchema.array().parse(apiResets);

    // Sort by resetAtTime descending (most recent first)
    resets.sort((a, b) => b.resetAtTime.getTime() - a.resetAtTime.getTime());

    return resets;
  }

  function useQueryFn() {
    const alertsResetsList = useInjectFeApiContext(
      FeApi.alertsV1ListAlertResetsRetrieve,
    );

    return async function queryFn(input: Input) {
      const { changedSinceDays } = input.filters;

      const result = await ok(alertsResetsList({ changedSinceDays }));

      const output = await AlertResetSchema.array().parseAsync(
        outputMapper(result.alerts_resets),
      );

      return output;
    };
  }

  export function useQuery(input: Input, { refetchInterval }: Params = {}) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      refetchInterval,
      queryKey: key(input),
      queryFn: () => queryFn(input),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
